import { useState } from "react";
import { Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface VideoHistory {
  id: string;
  video_id: string;
  video_title: string;
  video_thumbnail: string;
  created_at: string;
}

interface VideoHistoryCardProps {
  video: VideoHistory;
  onDelete: (id: string) => void;
  onClick: (video: VideoHistory) => void;
}

export const VideoHistoryCard = ({
  video,
  onDelete,
  onClick,
}: VideoHistoryCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = async () => {
    setIsAnimating(true);
    // Add animation class to parent drawer/sidebar if it exists
    const drawer = document.querySelector('[data-state="open"]');
    if (drawer) {
      drawer.classList.add('animate-slide-out-right');
    }
    
    // Wait for animation to complete before triggering onClick
    setTimeout(() => {
      onClick(video);
      setIsAnimating(false);
    }, 300); // Match animation duration
  };

  return (
    <Card
      className={`relative group transition-all duration-300 ease-in-out transform hover:bg-accent/5 cursor-pointer ${
        expanded ? "scale-105 z-10 bg-accent/5" : ""
      } ${isAnimating ? "animate-fade-out" : "animate-fade-in"}`}
      onClick={handleClick}
    >
      <CardContent
        className="p-3"
        onPointerDown={() => setExpanded(true)}
        onPointerUp={() => setExpanded(false)}
        onPointerLeave={() => setExpanded(false)}
      >
        {expanded ? (
          <div className="animate-in zoom-in duration-200 flex flex-col gap-3 w-full pointer-events-none">
            <img
              src={video.video_thumbnail || "/placeholder.svg"}
              alt={video.video_title || "Video thumbnail"}
              className="w-full aspect-video rounded-md object-cover"
            />
            <div className="text-sm font-medium">
              {video.video_title || "Untitled Video"}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <img
              src={video.video_thumbnail || "/placeholder.svg"}
              alt={video.video_title || "Video thumbnail"}
              className="w-12 h-12 rounded-md object-cover flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate hover:text-primary transition-colors">
                {video.video_title || "Untitled Video"}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/10 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(video.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};